// Libraries
import { memo, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import clsx from "clsx";
import moment from "moment";

// Components
import ReconcileNowDialog from "./ReconcileNowDialog";
import { TReconciliationStatusType } from "@/DataPages/types/inventory/models/reconciliation";

// Helpers
import { normalizeValue } from "../../../helpers/reconciliation";
import { getMaxDate } from "@/Common/helpers/Dates";

// Constants
import { DEFAULT_RECONCILIATION_STATUS } from "../../../constants/reconciliation";

// Types
import { TReconciliationType } from "../../../types/reconciliation";

type TReconcileNowButtonProps = {
	reconciliationStatus?: TReconciliationStatusType | null;
	disabled: boolean;
	isPendingReconciliation: boolean;
	onReconcile: (value: TReconciliationType[]) => void;
};

function ReconcileNowButton(props: Readonly<TReconcileNowButtonProps>) {
	const { reconciliationStatus, disabled, isPendingReconciliation, onReconcile } = props;

	const [openDialog, setOpenDialog] = useState(false);

	const dates = [reconciliationStatus?.newVehicleReconciliation?.endDate, reconciliationStatus?.usedVehicleReconciliation?.endDate]
		.filter(Boolean)
		.map((date) => moment(date));
	const lastReconciledAt = getMaxDate(dates);

	function handleOpenDialog() {
		setOpenDialog(true);
	}

	function getOptions() {
		const obj = Object.assign(DEFAULT_RECONCILIATION_STATUS, {
			newVehicleReconciliation: reconciliationStatus?.newVehicleReconciliation,
			usedVehicleReconciliation: reconciliationStatus?.usedVehicleReconciliation,
		});

		return Object.entries(obj).map(([key, value]) => ({
			...normalizeValue(key as keyof TReconciliationStatusType),
			disabled: value?.status === "RUNNING",
		}));
	}

	return (
		<Stack direction="row" justifyContent={"space-between"} alignItems={"flex-start"} mt={6} width={"fit-content"} spacing={2}>
			<Button
				onClick={handleOpenDialog}
				disabled={disabled}
				className={clsx({
					"!font-sans !text-[12px] !font-normal !text-white !rounded-[25px] !px-5 !py-[10px] !normal-case": true,
					"!bg-strong-gray": !disabled,
					"!bg-disabled": disabled,
				})}
				sx={{
					boxShadow: "none !important",
					"&:hover": {
						color: "white",
						boxShadow: "none !important",
					},
					minHeight: "10px !important",
					height: "15px !important",
				}}
			>
				Reconcile Now
			</Button>

			{lastReconciledAt && (
				<Typography variant={"subtitle2"} component={"span"} className={"!font-sans !text-[14.5] !font-normal !text-[#888888]"}>
					Last reconciled on {lastReconciledAt}
				</Typography>
			)}

			<ReconcileNowDialog
				isOpen={openDialog}
				isPendingReconciliation={isPendingReconciliation}
				options={getOptions()}
				onCloseDialog={() => setOpenDialog(false)}
				onReconcile={onReconcile}
			/>
		</Stack>
	);
}

export default memo(ReconcileNowButton);
